// What this page load knows about a setup it is saving or has saved, while the session still reports an empty device
// ('no-vault'), so Welcome, Create, Restore and the entrance header's network pill never offer a fresh start, or say
// "No vault here", over a stored setup. Two phases:
// - 'saving': a save has passed its last abandon point and installSetup is running, or it has resolved and the session
//   is adopting the setup (opening its ledger). A page the user reached meanwhile (browser Back, then Forward or a
//   door) shows a neutral "Saving your setup" screen (components/Ceremony.tsx SetupSaving), never the fresh doors and
//   never a claim that the setup could not be read. Cleared if installSetup is refused (nothing was stored).
// - 'unread': the setup is stored but the session could not read it back (R67 residual: the double fault). Those
//   screens offer a full reload of /locked instead (components/Ceremony.tsx), and the pill says "Setup saved". Set
//   when the saved screen finds the session no longer holding the setup: after a failed adoption on a page the user
//   left, only once the session's re-read has settled with the device still reported empty ('saving' holds until
//   then); after a failed adoption on the page still shown, or a lock that followed a successful adoption, at once.
// Both clear once the session has read a setup (locked) or holds one (unlocked), after which its own state is right
// again. A page load starts clear. A small store of its own, outside session.ts, so session behaviour app-wide is
// unchanged; its own module, so the header's pill can read it without importing the ceremony.

import { useSyncExternalStore } from 'react';
import { session, useSession } from './session';

export type SavedSetupPhase = 'saving' | 'unread';

let phase: SavedSetupPhase | null = null;
const listeners = new Set<() => void>();

function setPhase(value: SavedSetupPhase | null): void {
  if (phase === value) return;
  phase = value;
  for (const l of listeners) l();
}

session.subscribe(() => {
  const { state } = session.get();
  if (state === 'locked' || state === 'unlocked') setPhase(null);
});

/** Create and Restore call this just before installSetup: from here the save is not abandoned, and the session adopts it next. */
export function markSetupSaving(): void {
  setPhase('saving');
}

/** installSetup was refused: nothing was stored, so a save in flight no longer holds other screens off. */
export function markSetupNotSaved(): void {
  if (phase === 'saving') setPhase(null);
}

/** The setup is stored and the session could not read it back: the double-fault guard. */
export function markSetupSaved(): void {
  setPhase('unread');
}

/**
 * The saved screen found the session not holding the setup (a failed adoption, or a lock since) and started its re-read
 * of storage (`reread`, a session.lock()). A save the session was adopting keeps 'saving' until that read settles, since
 * it has not failed yet; 'unread' is armed only if the session still reports an empty device then. Without a save in
 * flight (a lock after a successful adoption, which cleared the phase) the guard is armed at once, so a screen
 * reached during the read is never the fresh doors; the session's own 'locked' then clears it if the read succeeds.
 * With `onScreen` (the page that saved is still the one shown, so no other page is) it is armed at once as well, so the
 * header's pill says "Setup saved" beside that page's saved screen rather than "Saving setup". Resolves once the read
 * has settled; never rejects.
 */
export function markSetupSavedAfter(reread: Promise<void>, onScreen = false): Promise<void> {
  if (phase !== 'saving' || onScreen) setPhase('unread');
  return reread
    .catch(() => {})
    .then(() => {
      if (session.get().state === 'no-vault') setPhase('unread');
    });
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** The phase, while the session reports an empty device; null otherwise. */
export function useSavedSetup(): SavedSetupPhase | null {
  const { state } = useSession();
  const current = useSyncExternalStore(
    subscribe,
    () => phase,
    () => phase,
  );
  return state === 'no-vault' ? current : null;
}

/** True while a setup saved by this page load is stored but the session reports an empty device. */
export function useSavedSetupUnread(): boolean {
  return useSavedSetup() === 'unread';
}
